import { FULL_NAV_LEVEL, SUPER_ADMIN_LEVEL, userLevelAtLeast } from '../constants/access-levels';

/**
 * Menu paths visible when the user has another permission (avoids DB migrations for every new screen).
 * Keys: route path as in ROUTES / sidebar. Values: any of these in access_permissions grants the key path.
 */
const IMPLIED_MENU_PATHS: Record<string, readonly string[]> = {
	'/system/status-maintenance': ['/system/types'],
};

/**
 * Collapsible parent: show "General Ledger" when the user has any child route under it (many DBs only store leaf paths).
 */
function impliedGeneralLedgerParent(path: string, access_permissions: string[]): boolean {
	if (path !== '/general-ledger') {
		return false;
	}
	return access_permissions.some((p) => typeof p === 'string' && p.startsWith('/general-ledger/'));
}

/**
 * New GL screens: show without a DB migration for every user group if they already have any other General Ledger route.
 */
function impliedGeneralLedgerGlImpact(path: string, access_permissions: string[]): boolean {
	if (path !== '/general-ledger/gl-impact') {
		return false;
	}
	return access_permissions.some(
		(p) =>
			typeof p === 'string' &&
			p.startsWith('/general-ledger/') &&
			p !== '/general-ledger/gl-impact'
	);
}

/**
 * True if `path` is listed in access_permissions, or implied by {@link IMPLIED_MENU_PATHS}.
 */
export function menuPathExplicitOrImplied(path: string, access_permissions: string[] | null | undefined): boolean {
	if (!access_permissions?.length) {
		return false;
	}
	if (access_permissions.includes(path)) {
		return true;
	}
	const impliedBy = IMPLIED_MENU_PATHS[path];
	if (impliedBy?.some((p) => access_permissions.includes(p))) {
		return true;
	}
	if (impliedGeneralLedgerParent(path, access_permissions)) {
		return true;
	}
	if (impliedGeneralLedgerGlImpact(path, access_permissions)) {
		return true;
	}
	return false;
}

export type MenuGrantUser = { user?: { fullaccess?: string | number; access_permissions?: string[] } } | null;

/**
 * Whether the editor may assign this menu path to another group's access (aligns with sidebar visibility rules).
 */
export function pathGrantableByEditor(path: string, user: MenuGrantUser): boolean {
	if (!user?.user) {
		return false;
	}
	if (userLevelAtLeast(user, SUPER_ADMIN_LEVEL)) {
		return true;
	}
	if (userLevelAtLeast(user, FULL_NAV_LEVEL)) {
		return true;
	}
	const access_permissions = user.user.access_permissions;
	if (access_permissions?.length && (path === '/storefront' || path.startsWith('/storefront/'))) {
		if (access_permissions.includes('/system/storefront-settings')) {
			return true;
		}
	}
	return menuPathExplicitOrImplied(path, access_permissions);
}
